// OA-percentage op projectniveau (SPEC_wijzigingen 2.4 — vervangt de teller uit SPEC_workflow 8.3).
//
//   kost(X) = werkposten en meerwerken van alle calculaties van project X
//           + extra werken       met toegerekend_project = X en telt_mee = ja, op welke calculatie ook
//           − aanrekenbare kosten met toegerekend_project = X en telt_mee = ja, op welke calculatie ook
//   percentage(X) = kost(X) / referentie(X)
//
// De noemer (gefactureerd, anders offertewaarde) blijft ongewijzigd. Pure functies.
import { normalizePNummer, getekendBedrag, type ExtraRegel } from './extraRegels.js';
import { round2 } from './schijven.js';

export interface CalcVoorPercentage {
  id: string;
  projectNummer: string;
  /** Naar de onderaannemer verstuurd (SPEC_workflow 5). Zolang niet, is een regel die ze aan een
   *  ander project toerekent daar "voorlopig" (SPEC_wijzigingen 2.5). */
  verstuurd: boolean;
  /** Werkposten (incl. gevelladder) + meerwerk van deze calculatie. */
  werkpostenEnMeerwerk: number;
  /** Deel van de werkposten en meerwerken dat open staat voor oplevering (27okt 4). */
  openWerkpostenEnMeerwerk?: number;
  extraRegels: ExtraRegel[];
  klantNaam?: string;
}

export interface ToegerekendeRegel {
  regel: ExtraRegel;
  calcId: string;
  calcProjectNummer: string;
  calcVerstuurd: boolean;
  calcKlantNaam?: string;
}

export interface ProjectKost {
  sleutel: string;
  projectNummer: string;
  werkpostenEnMeerwerk: number;
  extraWerkMee: number;
  aanrekeningMee: number;
  kost: number;
  /** Deel van de kost dat open staat voor oplevering: telt mee, maar wordt apart getoond (27okt 4). */
  kostOpen: number;
  calcIds: string[];
  /** Regels op calculaties van een ánder project die aan dit project toegerekend zijn. */
  vanElders: ToegerekendeRegel[];
  /** Bevat een meetellende regel uit een calculatie die nog niet verstuurd (goedgekeurd) is. */
  voorlopig: boolean;
}

/** Sleutel van "het project" van een calculatie: het P-nummer, of de calculatie zelf zonder P-nummer. */
export function projectSleutel(projectNummer: string | null | undefined, calcId: string): string {
  return normalizePNummer(projectNummer) || `calc:${calcId}`;
}

function leeg(sleutel: string, projectNummer: string): ProjectKost {
  return {
    sleutel,
    projectNummer,
    werkpostenEnMeerwerk: 0,
    extraWerkMee: 0,
    aanrekeningMee: 0,
    kost: 0,
    kostOpen: 0,
    calcIds: [],
    vanElders: [],
    voorlopig: false,
  };
}

/**
 * Berekent kost(X) voor elk project dat in de calculaties voorkomt, als eigen project of
 * als toegerekend project. Een bedrag telt maximaal één keer: enkel in het project waar het
 * aan toegerekend is, en enkel als de vlag aan staat.
 */
export function berekenProjectKosten(calcs: CalcVoorPercentage[]): Map<string, ProjectKost> {
  const out = new Map<string, ProjectKost>();
  const get = (sleutel: string, projectNummer: string) => {
    let pk = out.get(sleutel);
    if (!pk) {
      pk = leeg(sleutel, projectNummer);
      out.set(sleutel, pk);
    }
    return pk;
  };

  for (const c of calcs) {
    const eigen = projectSleutel(c.projectNummer, c.id);
    const pk = get(eigen, normalizePNummer(c.projectNummer));
    pk.werkpostenEnMeerwerk += c.werkpostenEnMeerwerk || 0;
    pk.kostOpen += c.openWerkpostenEnMeerwerk || 0;
    pk.calcIds.push(c.id);

    for (const r of c.extraRegels) {
      const doel = normalizePNummer(r.toegerekendProject) || eigen;
      const target = get(doel, doel.startsWith('calc:') ? '' : doel);
      if (doel !== eigen) {
        target.vanElders.push({
          regel: r,
          calcId: c.id,
          calcProjectNummer: normalizePNummer(c.projectNummer),
          calcVerstuurd: c.verstuurd,
          calcKlantNaam: c.klantNaam,
        });
      }
      if (r.teltMeeInPercentage !== true) continue;
      if (r.soort === 'extra_werk') {
        target.extraWerkMee += r.bedrag;
        if (r.oplevering?.status === 'open') target.kostOpen += r.bedrag;
      } else target.aanrekeningMee += r.bedrag;
      if (doel !== eigen && !c.verstuurd) target.voorlopig = true;
    }
  }

  for (const pk of out.values()) {
    pk.werkpostenEnMeerwerk = round2(pk.werkpostenEnMeerwerk);
    pk.extraWerkMee = round2(pk.extraWerkMee);
    pk.aanrekeningMee = round2(pk.aanrekeningMee);
    pk.kost = round2(pk.werkpostenEnMeerwerk + pk.extraWerkMee - pk.aanrekeningMee);
    pk.kostOpen = round2(pk.kostOpen);
  }
  return out;
}

/** Percentage (0–100) of null zonder bruikbare noemer. */
export function oaPercentage(kost: number, noemer: number | null | undefined): number | null {
  if (noemer == null || !(noemer > 0)) return null;
  return (kost / noemer) * 100;
}

/** Bijdrage van één regel aan kost(X) — gebruikt voor de live "P-A gaat van x% naar y%"-preview. */
export function bijdrageAanProject(regel: ExtraRegel): number {
  return regel.teltMeeInPercentage === true ? getekendBedrag(regel) : 0;
}
